// lib/pricing/hostQueue.ts
// ONE gate in front of EVERY request the widget makes to a B2B host's own
// server (per-customer prices, offer HTML). Browser-only.
//
// WHY THIS EXISTS. These hosts process one request per logged-in session at a
// time — a second request that arrives while the first is still computing gets
// REFUSED, not queued. Measured on syfak (2026-07-27): two concurrent 500-id
// pricing requests → the first returned 200, the second **HTTP 503**, and
// because the caller used Promise.all the successful 500 prices were discarded
// with it. Cards fell back to catalogue prices, offers vanished, and the host
// site felt laggy. The same shape, at catalogue scale, is what forced the
// price-map kill switch.
//
// Serialising per-chunk inside one call site was NOT enough — an audit found
// three more ways to overlap:
//   1. ACROSS SEARCHES — typing re-runs the effect; the old request keeps
//      running on the host (React cleanup stops setState, not the request).
//   2. OFFER PREFETCH — hovering an offer card fired a request to the same host
//      while prices were still loading; a row of offer cards fired several.
//      That prefetch was REMOVED on 2026-07-28: with one lane, a mouse sweep
//      queued a request per card and the user's actual click landed behind the
//      whole trail. See lib/offers/offerHtml.ts.
//   3. THE PRICE MAP — 2 workers, and its adaptive retry fires halves in
//      parallel. Off for syfak, but LIVE for acalight.
// Fixing those one at a time would leave the next feature free to reintroduce
// the problem. A gate every path must pass through cannot be bypassed by
// accident.
//
// Deliberately per-HOST-key, not global: two different clients embedded on two
// different sites have unrelated servers and must not block each other.

/** Serial queues, one per host key. Module-level so every call site shares them. */
const queues = new Map<string, Promise<unknown>>();

/**
 * Run `task` when the host key's queue is free, and hold the queue until it
 * settles. Returns exactly what `task` returns; rejections propagate to the
 * caller unchanged (and do NOT poison the queue for the next task).
 *
 * Ordering is FIFO — callers are served in the order they enqueue.
 */
export function enqueueHostRequest<T>(hostKey: string, task: () => Promise<T>): Promise<T> {
  const previous = queues.get(hostKey) ?? Promise.resolve();
  // `.catch` BEFORE chaining: a failed predecessor must not reject the whole
  // chain, or one 503 would cascade into every queued request behind it.
  const run = previous.catch(() => {}).then(task);
  // Track the settled-ness of `run`, not its value — same reason.
  queues.set(hostKey, run.catch(() => {}));
  return run;
}

/**
 * Key a queue by the host we'll actually talk to. Same-origin relative URLs
 * ('/index.php?…') all resolve to the embedding page's host, so they share one
 * queue; an absolute URL keys by its origin. Falls back to the raw string if it
 * can't be parsed — never throws, since this only picks a bucket.
 */
export function hostKeyForUrl(url: string): string {
  try {
    if (/^https?:\/\//i.test(url)) return new URL(url).origin;
  } catch {
    /* fall through */
  }
  return typeof window !== 'undefined' ? window.location.origin : 'self';
}
